import { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import {
  legalDocumentBodySx,
  legalDocumentCardSx,
  legalPageContainerSx,
  legalPageHeaderSx,
  legalPageMetaSx,
  legalPageShellSx,
  legalPageTitleSx,
} from "./styles";

const CONTACT_EMAIL = "hello@ommuse.com";

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    document.title = "Privacy Policy - OmMuse";
  }, []);

  return (
    <Box sx={legalPageShellSx}>
      <Box sx={legalPageContainerSx}>
        <Box sx={legalPageHeaderSx}>
          <Typography sx={legalPageTitleSx}>OmMuse Privacy Policy</Typography>
          <Typography sx={legalPageMetaSx}>
            Last Updated: April 3, 2026
          </Typography>
        </Box>
        <Box sx={legalDocumentCardSx}>
          <Box sx={legalDocumentBodySx}>
            <ul>
              <li>
                <strong>1. Introduction</strong>
              </li>
            </ul>
            <p>
              OmMuse, LLC (&ldquo;OmMuse,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) is committed to
              protecting your privacy and handling your personal data in a
              transparent and secure manner. This Privacy Policy describes how we
              collect, use, disclose, and safeguard your information when you access
              or use the OmMuse platform, website, and related services
              (collectively, the &ldquo;Platform&rdquo;).
            </p>
            <p>
              This Privacy Policy applies globally and is designed to comply with
              applicable data protection laws, including but not limited to:
            </p>
            <ol>
              <li>The General Data Protection Regulation (GDPR) (EU)</li>
              <li>The UK GDPR</li>
              <li>The California Consumer Privacy Act (CCPA/CPRA)</li>
              <li>Other applicable U.S. and international privacy laws</li>
            </ol>
            <p>
              By accessing or using the Platform, you acknowledge that you have read
              and understood this Privacy Policy.
            </p>

            <ul>
              <li>
                <strong>2. Information We Collect</strong>
              </li>
            </ul>
            <p>
              We collect information you provide directly, information collected
              automatically, and information from third parties.
            </p>
            <p>
              <strong>A. Personal Information (PII)</strong>
            </p>
            <p>We may collect the following:</p>
            <ol>
              <li>Name, username, and profile details</li>
              <li>Email address and contact information</li>
              <li>Account login credentials</li>
              <li>Billing and payment information (processed by third-party providers)</li>
              <li>Creator-specific information (artist name, business details, uploaded content metadata)</li>
              <li>Communications with us (support requests, emails, etc.)</li>
            </ol>
            <p>
              <strong>B. Content and Collaboration Data</strong>
            </p>
            <p>Given the nature of OmMuse, we may collect and store:</p>
            <ol>
              <li>Audio files, stems, lyrics, artwork, and project files</li>
              <li>Collaboration activity and version history</li>
              <li>Metadata associated with uploaded content</li>
            </ol>
            <p>
              <strong>Important:</strong> You retain ownership of your content. We
              process this data solely to provide and improve the Platform.
            </p>
            <p>
              <strong>C. Automatically Collected Information (Non-PII)</strong>
            </p>
            <p>We automatically collect:</p>
            <ol>
              <li>IP address and approximate location</li>
              <li>Device and browser type</li>
              <li>Usage data and activity logs</li>
              <li>Referring URLs and session data</li>
              <li>Cookies and tracking data</li>
            </ol>

            <ul>
              <li>
                <strong>3. How We Use Your Information</strong>
              </li>
            </ul>
            <p>We use your information to:</p>
            <ol>
              <li>Provide, operate, and maintain the Platform</li>
              <li>Enable collaboration and content sharing</li>
              <li>Process transactions and manage subscriptions</li>
              <li>Improve platform performance and user experience</li>
              <li>Communicate with you (support, updates, service notices)</li>
              <li>Ensure security, fraud prevention, and compliance</li>
              <li>Comply with legal obligations</li>
            </ol>
            <p>We do not sell your personal data.</p>

            <ul>
              <li>
                <strong>4. Legal Bases for Processing (GDPR/UK GDPR)</strong>
              </li>
            </ul>
            <p>
              If you are located in the EEA or UK, we process your personal data
              based on:
            </p>
            <ol>
              <li>Performance of a contract (providing the Platform)</li>
              <li>Legitimate interests (improving services, security)</li>
              <li>Consent (marketing communications, where applicable)</li>
              <li>Legal obligations</li>
            </ol>

            <ul>
              <li>
                <strong>5. Sharing and Disclosure of Information</strong>
              </li>
            </ul>
            <p>We may share your information with:</p>
            <p>
              <strong>A. Service Providers</strong>
            </p>
            <p>Third parties who help operate our Platform, including:</p>
            <ol>
              <li>Cloud storage providers</li>
              <li>Payment processors</li>
              <li>Analytics providers</li>
              <li>Security and infrastructure vendors</li>
            </ol>
            <p>These providers are contractually obligated to protect your data.</p>
            <p>
              <strong>B. Other Users</strong>
            </p>
            <p>
              Certain information (e.g., profile, shared content) is visible to
              collaborators based on your permissions.
            </p>
            <p>
              <strong>C. Legal and Compliance</strong>
            </p>
            <p>We may disclose information:</p>
            <ol>
              <li>To comply with legal obligations</li>
              <li>To enforce our Terms</li>
              <li>To protect rights, safety, and security</li>
            </ol>
            <p>
              <strong>D. Business Transfers</strong>
            </p>
            <p>
              In connection with a merger, acquisition, or sale of assets, your
              information may be transferred.
            </p>

            <ul>
              <li>
                <strong>6. International Data Transfers</strong>
              </li>
            </ul>
            <p>
              Your information may be transferred to and processed in countries
              outside your jurisdiction, including the United States.
            </p>
            <p>Where required, we implement safeguards such as:</p>
            <ol>
              <li>Standard Contractual Clauses (SCCs)</li>
              <li>Data processing agreements</li>
              <li>Equivalent legal mechanisms</li>
            </ol>

            <ul>
              <li>
                <strong>7. Data Retention</strong>
              </li>
            </ul>
            <p>We retain your information:</p>
            <ol>
              <li>As long as your account is active</li>
              <li>As necessary to provide services</li>
              <li>As required by legal, tax, or regulatory obligations</li>
            </ol>
            <p>
              We may retain limited data after account deletion for compliance and
              dispute resolution.
            </p>

            <ul>
              <li>
                <strong>8. Your Privacy Rights</strong>
              </li>
            </ul>
            <p>Depending on your location, you may have the right to:</p>
            <p>
              <strong>Global Rights</strong>
            </p>
            <ol>
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete your data</li>
              <li>Restrict or object to processing</li>
              <li>Data portability</li>
            </ol>
            <p>
              <strong>California (CCPA/CPRA) Rights</strong>
            </p>
            <ol>
              <li>Right to know what data is collected</li>
              <li>Right to delete personal data</li>
              <li>Right to opt out of &ldquo;sale&rdquo; (we do not sell data)</li>
              <li>Right to non-discrimination</li>
            </ol>
            <p>
              <strong>EEA/UK Rights</strong>
            </p>
            <ol>
              <li>Withdraw consent at any time</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ol>
            <p>
              To exercise rights, contact:{" "}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>

            <ul>
              <li>
                <strong>9. Cookies and Tracking Technologies</strong>
              </li>
            </ul>
            <p>We use cookies and similar technologies to:</p>
            <ol>
              <li>Authenticate users</li>
              <li>Improve functionality</li>
              <li>Analyze usage</li>
            </ol>
            <p>You can control cookies through your browser settings.</p>

            <ul>
              <li>
                <strong>10. Data Security</strong>
              </li>
            </ul>
            <p>
              We implement reasonable administrative, technical, and physical
              safeguards, including:
            </p>
            <ol>
              <li>Encryption (in transit and at rest where applicable)</li>
              <li>Access controls</li>
              <li>Secure cloud infrastructure</li>
            </ol>
            <p>However, no system is completely secure.</p>

            <ul>
              <li>
                <strong>11. Children&apos;s Privacy</strong>
              </li>
            </ul>
            <p>
              The Platform is not intended for users under 13 years of age. We do
              not knowingly collect personal data from children.
            </p>
            <p>If we become aware of such data, we will delete it.</p>

            <ul>
              <li>
                <strong>12. Third-Party Services and Links</strong>
              </li>
            </ul>
            <p>
              The Platform may contain links to third-party services. We are not
              responsible for their privacy practices.
            </p>

            <ul>
              <li>
                <strong>13. Marketing Communications</strong>
              </li>
            </ul>
            <p>You may opt out of marketing communications at any time by:</p>
            <ol>
              <li>Clicking &ldquo;unsubscribe&rdquo; in emails</li>
              <li>Contacting us at {CONTACT_EMAIL}</li>
            </ol>

            <ul>
              <li>
                <strong>14. Changes to This Policy</strong>
              </li>
            </ul>
            <p>
              We may update this Privacy Policy periodically. Changes will be
              reflected by the &ldquo;Last Updated&rdquo; date.
            </p>
            <p>
              Continued use of the Platform constitutes acceptance of updates.
            </p>

            <ul>
              <li>
                <strong>15. Contact Information</strong>
              </li>
            </ul>
            <p>For questions or requests:</p>
            <p>
              <strong>OmMuse, LLC</strong>
              <br />
              Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
